import React, { useEffect, useReducer, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { posToDOMRect } from '@tiptap/core'
import { NodeSelection } from '@tiptap/pm/state'
import FormatAlignLeftIcon from '@material-ui/icons/FormatAlignLeft'
import FormatAlignCenterIcon from '@material-ui/icons/FormatAlignCenter'
import FormatAlignRightIcon from '@material-ui/icons/FormatAlignRight'
import FormatAlignJustifyIcon from '@material-ui/icons/FormatAlignJustify'
import useFloatingPosition from './useFloatingPosition'
import { getPortalContainer } from './portal'
import { turnIntoCommands } from './blockCommands'
import useStyles from './styles'


const markButtons = [
  { mark: 'bold', label: 'B', title: 'Bold (Ctrl+B)', style: { fontWeight: 700 }, toggle: chain => chain.toggleBold() },
  { mark: 'italic', label: 'i', title: 'Italic (Ctrl+I)', style: { fontStyle: 'italic', fontFamily: 'Georgia, serif' }, toggle: chain => chain.toggleItalic() },
  { mark: 'underline', label: 'U', title: 'Underline (Ctrl+U)', style: { textDecoration: 'underline' }, toggle: chain => chain.toggleUnderline() },
  { mark: 'strike', label: 'S', title: 'Strikethrough (Ctrl+Shift+S)', style: { textDecoration: 'line-through' }, toggle: chain => chain.toggleStrike() },
  { mark: 'code', label: '</>', title: 'Inline code (Ctrl+E)', style: { fontFamily: 'monospace', fontSize: 12 }, toggle: chain => chain.toggleCode() }
]

const textColors = [
  { name: 'Default', color: null },
  { name: 'Gray', color: '#6F6F6F' },
  { name: 'Purple', color: '#7600D3' },
  { name: 'Green', color: '#059D8F' },
  { name: 'Yellow', color: '#A7A229' },
  { name: 'Red', color: '#F25942' }
]

const highlightColors = [
  { name: 'None', color: null },
  { name: 'Gray', color: '#F4F4F4' },
  { name: 'Purple', color: '#E7DBF1' },
  { name: 'Green', color: '#E3FAF8' },
  { name: 'Yellow', color: '#F8F18C' },
  { name: 'Red', color: '#F25942' }
]

const isTextColorActive = (editor, color) => (
  color ? editor.isActive('textStyle', { color }) : !editor.getAttributes('textStyle').color
)

const isHighlightActive = (editor, color) => (
  color ? editor.isActive('highlight', { color }) : !editor.isActive('highlight')
)

const setTextColor = (editor, color) => {
  const chain = editor.chain().focus()
  if (color) {
    chain.setColor(color).run()
  } else {
    chain.unsetColor().run()
  }
}

const setHighlight = (editor, color) => {
  const chain = editor.chain().focus()
  if (color) {
    chain.setHighlight({ color }).run()
  } else {
    chain.unsetHighlight().run()
  }
}

const alignButtons = [
  { align: 'left', title: 'Align left', Icon: FormatAlignLeftIcon },
  { align: 'center', title: 'Align center', Icon: FormatAlignCenterIcon },
  { align: 'right', title: 'Align right', Icon: FormatAlignRightIcon },
  { align: 'justify', title: 'Justify', Icon: FormatAlignJustifyIcon }
]

// Left is the default, so it is stored as "no alignment"
const isAlignActive = (editor, align) => (
  align === 'left' ?
    !alignButtons.some(button => button.align !== 'left' && editor.isActive({ textAlign: button.align })) :
    editor.isActive({ textAlign: align })
)

const setAlign = (editor, align) => {
  const chain = editor.chain().focus()
  if (align === 'left') {
    chain.unsetTextAlign().run()
  } else {
    chain.setTextAlign(align).run()
  }
}

const hasTextSelection = (editor) => {
  const { state } = editor
  const { selection } = state
  if (selection.empty || selection instanceof NodeSelection) {
    return false
  }
  return state.doc.textBetween(selection.from, selection.to, ' ').trim().length > 0
}

// Formatting toolbar shown above selected text, like Notion's.
const BubbleMenu = ({ editor, onAskAi }) => {
  const localClasses = useStyles()
  const menuRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [linkMode, setLinkMode] = useState(false)
  const [linkUrl, setLinkUrl] = useState('')
  const [turnIntoOpen, setTurnIntoOpen] = useState(false)
  const [colorOpen, setColorOpen] = useState(false)
  const [, refresh] = useReducer(count => count + 1, 0)

  useEffect(() => {
    const menuHasFocus = () => menuRef.current && menuRef.current.contains(document.activeElement)
    const update = () => {
      const visible = hasTextSelection(editor) && (editor.view.hasFocus() || menuHasFocus())
      setOpen(visible)
      if (!visible) {
        setLinkMode(false)
        setTurnIntoOpen(false)
        setColorOpen(false)
      }
      refresh()
    }
    const handleBlur = ({ event }) => {
      if (event && event.relatedTarget && menuRef.current && menuRef.current.contains(event.relatedTarget)) {
        return
      }
      setOpen(false)
      setLinkMode(false)
      setTurnIntoOpen(false)
      setColorOpen(false)
    }
    editor.on('selectionUpdate', update)
    editor.on('transaction', refresh)
    editor.on('focus', update)
    editor.on('blur', handleBlur)
    return () => {
      editor.off('selectionUpdate', update)
      editor.off('transaction', refresh)
      editor.off('focus', update)
      editor.off('blur', handleBlur)
    }
  }, [editor])

  useFloatingPosition(
    menuRef,
    () => ({
      getBoundingClientRect: () => {
        const { from, to } = editor.state.selection
        return posToDOMRect(editor.view, from, to)
      },
      contextElement: editor.view.dom
    }),
    { open, placement: 'top-start', gap: 8 },
    [editor]
  )

  if (!open) {
    return null
  }

  const openLinkEditor = () => {
    setLinkUrl(editor.getAttributes('link').href || '')
    setTurnIntoOpen(false)
    setColorOpen(false)
    setLinkMode(true)
  }

  const applyLink = () => {
    const url = linkUrl.trim()
    const chain = editor.chain().focus().extendMarkRange('link')
    if (url) {
      chain.setLink({ href: url }).run()
    } else {
      chain.unsetLink().run()
    }
    setLinkMode(false)
  }

  const activeBlock = turnIntoCommands.find(item => item.isActive(editor)) || turnIntoCommands[0]
  const keepFocus = event => event.preventDefault()

  return ReactDOM.createPortal(
    <div className={`${localClasses.popover} ${localClasses.bubbleMenu}`} ref={menuRef}>
      {
        linkMode ?
        <div className={localClasses.linkEditor}>
          <input
            autoFocus
            className={localClasses.linkInput}
            placeholder='Paste link and press Enter…'
            value={linkUrl}
            onChange={event => setLinkUrl(event.target.value)}
            onBlur={(event) => {
              if (!menuRef.current || !menuRef.current.contains(event.relatedTarget)) {
                setLinkMode(false)
                setOpen(editor.view.hasFocus() && hasTextSelection(editor))
              }
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                applyLink()
              } else if (event.key === 'Escape') {
                event.preventDefault()
                setLinkMode(false)
                editor.commands.focus()
              }
            }}
          />
          <button type='button' className={localClasses.menuButton} onClick={applyLink}>Apply</button>
          {
            editor.isActive('link') &&
            <button
              type='button'
              className={localClasses.menuButton}
              onClick={() => {
                editor.chain().focus().extendMarkRange('link').unsetLink().run()
                setLinkMode(false)
              }}
            >
              Remove
            </button>
          }
        </div> :
        <>
          <button
            type='button'
            className={`${localClasses.menuButton} ai`}
            onMouseDown={keepFocus}
            onClick={onAskAi}
            title='Ask AI (Ctrl+J)'
          >
            ✦ Ask AI
          </button>
          <span className={localClasses.menuDivider} />
          <button
            type='button'
            className={localClasses.menuButton}
            onMouseDown={keepFocus}
            onClick={() => {
              setTurnIntoOpen(!turnIntoOpen)
              setColorOpen(false)
            }}
          >
            {activeBlock.title} ▾
          </button>
          <span className={localClasses.menuDivider} />
          <button
            type='button'
            className={localClasses.menuButton}
            data-active={editor.isActive('link')}
            onMouseDown={keepFocus}
            onClick={openLinkEditor}
            title='Link'
            style={{ textDecoration: 'underline' }}
          >
            Link
          </button>
          <span className={localClasses.menuDivider} />
          {
            markButtons.map(button => (
              <button
                key={button.mark}
                type='button'
                className={localClasses.menuButton}
                data-active={editor.isActive(button.mark)}
                onMouseDown={keepFocus}
                onClick={() => button.toggle(editor.chain().focus()).run()}
                title={button.title}
                style={button.style}
              >
                {button.label}
              </button>
            ))
          }
          <span className={localClasses.menuDivider} />
          <button
            type='button'
            className={localClasses.menuButton}
            data-active={colorOpen}
            onMouseDown={keepFocus}
            onClick={() => {
              setColorOpen(!colorOpen)
              setTurnIntoOpen(false)
            }}
            title='Text color and highlight'
          >
            <span
              className={localClasses.colorSample}
              style={{
                color: editor.getAttributes('textStyle').color,
                backgroundColor: editor.getAttributes('highlight').color
              }}
            >
              A
            </span> ▾
          </button>
          <span className={localClasses.menuDivider} />
          {
            alignButtons.map(({ align, title, Icon }) => (
              <button
                key={align}
                type='button'
                className={`${localClasses.menuButton} icon`}
                data-active={isAlignActive(editor, align)}
                onMouseDown={keepFocus}
                onClick={() => setAlign(editor, align)}
                title={title}
              >
                <Icon fontSize='small' />
              </button>
            ))
          }
          <span className={localClasses.menuDivider} />
          <button
            type='button'
            className={localClasses.menuButton}
            onMouseDown={keepFocus}
            onClick={() => editor.chain().focus().unsetAllMarks().run()}
            title='Clear formatting'
          >
            T̸
          </button>
          {
            colorOpen &&
            <div className={`${localClasses.popover} ${localClasses.colorMenu}`}>
              <div className={localClasses.menuLabel}>Text color</div>
              <div className={localClasses.colorRow}>
                {
                  textColors.map(({ name, color }) => (
                    <button
                      key={name}
                      type='button'
                      className={localClasses.colorSwatch}
                      data-active={isTextColorActive(editor, color)}
                      onMouseDown={keepFocus}
                      onClick={() => setTextColor(editor, color)}
                      title={name}
                      style={{ color: color || 'inherit' }}
                    >
                      A
                    </button>
                  ))
                }
              </div>
              <div className={localClasses.menuLabel}>Highlight</div>
              <div className={localClasses.colorRow}>
                {
                  highlightColors.map(({ name, color }) => (
                    <button
                      key={name}
                      type='button'
                      className={localClasses.colorSwatch}
                      data-active={isHighlightActive(editor, color)}
                      onMouseDown={keepFocus}
                      onClick={() => setHighlight(editor, color)}
                      title={name}
                      style={{ backgroundColor: color || 'transparent' }}
                    >
                      A
                    </button>
                  ))
                }
              </div>
            </div>
          }
          {
            turnIntoOpen &&
            <div className={`${localClasses.popover} ${localClasses.turnIntoMenu}`}>
              <div className={localClasses.menuLabel}>Turn into</div>
              {
                turnIntoCommands.map(item => (
                  <div
                    key={item.id}
                    className={localClasses.slashItem}
                    data-selected={item === activeBlock}
                    onMouseDown={keepFocus}
                    onClick={() => {
                      item.run(editor.chain().focus())
                      setTurnIntoOpen(false)
                    }}
                  >
                    <div className={`${localClasses.slashIcon} small`}>{item.icon}</div>
                    <div className={localClasses.slashTitle}>{item.title}</div>
                  </div>
                ))
              }
            </div>
          }
        </>
      }
    </div>,
    getPortalContainer(editor)
  )
}

export default BubbleMenu
